/**
 * ExpenseReportPage Component
 * Year-end Schedule E expense report with property/year selector and CSV export.
 * Route: /expense-reports
 */

import { useState, useEffect, useCallback } from 'react';
import {
  listProperties,
  expenseReportsApi,
  type PropertyWithStats,
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { HelpBlock, HelpTooltip } from '../components/ui/HelpTooltip';
import type { YearEndReport } from '../types/expense.types';
import { formatAmount } from '../utils/expense.utils';
import { formatDateTime } from '../utils/date';

// -------------------------------------------------------------------------
// Helpers
// -------------------------------------------------------------------------

function getCurrentYear(): number {
  return new Date().getFullYear();
}

function buildYearOptions(): number[] {
  const current = getCurrentYear();
  const years: number[] = [];
  for (let y = 2020; y <= current + 1; y++) {
    years.push(y);
  }
  return years;
}

function downloadCsv(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// -------------------------------------------------------------------------
// Sub-components
// -------------------------------------------------------------------------

interface ReportTableProps {
  report: YearEndReport;
}

function ReportTable({ report }: ReportTableProps) {
  return (
    <div className="overflow-x-auto">
      <table
        data-testid="expense-report-table"
        className="w-full border-collapse"
        aria-label="Schedule E expense category breakdown"
      >
        <thead>
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b border-border bg-accent/50">
              <span className="inline-flex items-center gap-1">
                IRS Schedule E Line
                <HelpTooltip text="The line number on IRS Schedule E where this category of expense is reported. Transfer these totals to the corresponding lines on your tax return." />
              </span>
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b border-border bg-accent/50">Category</th>
            <th className="px-4 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b border-border bg-accent/50">Expenses</th>
            <th className="px-4 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b border-border bg-accent/50">Total Amount</th>
          </tr>
        </thead>
        <tbody>
          {report.categories.map((cat) => (
            <tr key={cat.categoryId}>
              <td className="px-4 py-3 text-sm text-foreground border-b border-border">
                {cat.scheduleELine ?? <span className="text-muted-foreground">—</span>}
              </td>
              <td className="px-4 py-3 text-sm text-foreground border-b border-border">{cat.categoryName}</td>
              <td className="px-4 py-3 text-sm text-foreground border-b border-border text-right">{cat.expenseCount}</td>
              <td className="px-4 py-3 text-sm font-semibold text-foreground border-b border-border text-right">{formatAmount(cat.totalAmount)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-accent/50">
            <td
              colSpan={2}
              className="px-4 py-3 text-sm font-bold text-foreground border-b border-border"
            >
              Total
            </td>
            <td className="px-4 py-3 text-sm font-bold text-foreground border-b border-border text-right">
              {report.expenseCount}
            </td>
            <td
              className="px-4 py-3 text-sm font-bold text-foreground border-b border-border text-right"
              data-testid="expense-report-total"
              aria-label={`Total expenses: ${formatAmount(report.totalAmount)}`}
            >
              {formatAmount(report.totalAmount)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// -------------------------------------------------------------------------
// Main Page
// -------------------------------------------------------------------------

export function ExpenseReportPage() {
  const [properties, setProperties] = useState<PropertyWithStats[]>([]);
  const [loadingProperties, setLoadingProperties] = useState(true);
  const [propertiesError, setPropertiesError] = useState<string | null>(null);

  const [selectedYear, setSelectedYear] = useState<number>(getCurrentYear());
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>('');

  const [report, setReport] = useState<YearEndReport | null>(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  const [exportingCsv, setExportingCsv] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const yearOptions = buildYearOptions();

  const loadProperties = useCallback(async () => {
    try {
      setLoadingProperties(true);
      setPropertiesError(null);
      const result = await listProperties();
      setProperties(result.properties);
    } catch (err) {
      setPropertiesError(err instanceof Error ? err.message : 'Failed to load properties');
    } finally {
      setLoadingProperties(false);
    }
  }, []);

  useEffect(() => {
    loadProperties();
  }, [loadProperties]);

  const handleGenerateReport = async () => {
    if (!selectedPropertyId) return;
    setGeneratingReport(true);
    setReportError(null);
    setReport(null);
    try {
      const result = await expenseReportsApi.yearEnd({
        year: selectedYear,
        propertyId: selectedPropertyId,
      });
      setReport(result);
    } catch (err) {
      setReportError(err instanceof Error ? err.message : 'Failed to generate report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleExportCsv = async () => {
    if (!selectedPropertyId) return;
    setExportingCsv(true);
    setExportError(null);
    try {
      const result = await expenseReportsApi.export({
        startDate: `${selectedYear}-01-01`,
        endDate: `${selectedYear}-12-31`,
        propertyId: selectedPropertyId,
      });
      const propertyName = properties.find((p) => p.id === selectedPropertyId)?.name ?? selectedPropertyId;
      const safeName = propertyName.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      downloadCsv(result.csv, `expenses-${selectedYear}-${safeName}.csv`);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Failed to export CSV');
    } finally {
      setExportingCsv(false);
    }
  };

  const selectedProperty = properties.find((p) => p.id === selectedPropertyId);

  if (loadingProperties) {
    return (
      <div className="max-w-4xl mx-auto pb-6" data-testid="expense-report-page">
        <div className="px-[var(--page-padding-x)] pt-5 space-y-3">
          <Skeleton className="h-20 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto pb-6" data-testid="expense-report-page">
      <div className="px-[var(--page-padding-x)] pt-5">
        {/* Help block */}
        <div className="mb-4">
          <HelpBlock
            summary="Generate a year-end summary of your expenses organized by IRS Schedule E categories. Use this for tax filing or share the CSV export with your accountant."
            details={"What is Schedule E: IRS Schedule E is the tax form where landlords report rental income and expenses. This report groups your expenses by the standard Schedule E line items (advertising, insurance, repairs, etc.) so you can transfer the totals directly to your tax return.\n\nImportant: Only confirmed expenses are included. If you have expenses waiting in the Confirm Expenses tab, they won't appear here until you review and confirm them.\n\nThe CSV export includes every individual expense for the selected year and property, which your accountant can use for detailed records."}
          />
        </div>

        {propertiesError && (
          <div
            role="alert"
            className="p-3 mb-4 bg-destructive/5 border border-destructive/20 rounded-md text-destructive text-sm flex items-center gap-3"
          >
            <span className="flex-1">{propertiesError}</span>
            <Button variant="link" size="sm" onClick={loadProperties}>Retry</Button>
          </div>
        )}

        {/* Controls bar */}
        <div className="flex flex-wrap gap-3 items-end p-5 rounded-lg border border-border bg-card mb-5">
          <div className="flex flex-col gap-1 min-w-[180px]">
            <label
              htmlFor="expense-report-year"
              className="text-xs font-medium text-foreground"
            >
              Tax Year
            </label>
            <select
              id="expense-report-year"
              data-testid="expense-report-year-select"
              value={selectedYear}
              onChange={(e) => {
                setSelectedYear(Number(e.target.value));
                setReport(null);
              }}
              className="px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground outline-none min-h-[44px]"
              aria-label="Select tax year"
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1 min-w-[180px]">
            <label
              htmlFor="expense-report-property"
              className="text-xs font-medium text-foreground"
            >
              Property
            </label>
            <select
              id="expense-report-property"
              data-testid="expense-report-property-select"
              value={selectedPropertyId}
              onChange={(e) => {
                setSelectedPropertyId(e.target.value);
                setReport(null);
              }}
              className="px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground outline-none min-h-[44px]"
              aria-label="Select property"
              aria-required="true"
            >
              <option value="">-- Select a property --</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <Button
            size="sm"
            data-testid="expense-report-generate"
            onClick={handleGenerateReport}
            disabled={!selectedPropertyId || generatingReport}
            aria-label={`Generate ${selectedYear} year-end report`}
          >
            {generatingReport ? 'Generating...' : 'Generate Report'}
          </Button>
        </div>

        {reportError && (
          <div
            role="alert"
            className="p-3 mb-4 bg-destructive/5 border border-destructive/20 rounded-md text-destructive text-sm"
          >
            {reportError}
          </div>
        )}

        {generatingReport && (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full rounded-lg" />
            ))}
          </div>
        )}

        {/* Report result */}
        {report && !generatingReport && (
          <div data-testid="expense-report-result" className="rounded-lg border border-border bg-card p-4">
            {/* Report header */}
            <div className="flex justify-between items-start flex-wrap gap-3 mb-5">
              <div>
                <h2 className="m-0 text-xl font-bold text-foreground">
                  Schedule E Expense Summary — {report.year}
                </h2>
                {selectedProperty && (
                  <p className="mt-1 m-0 text-sm text-muted-foreground">
                    {selectedProperty.name}
                  </p>
                )}
                <p className="mt-2 m-0 text-xs text-muted-foreground">
                  Generated {formatDateTime(report.generatedAt, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>

              <div className="flex gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="expense-report-export-csv"
                  onClick={handleExportCsv}
                  disabled={exportingCsv}
                  aria-label={`Export ${selectedYear} expenses as CSV`}
                >
                  {exportingCsv ? 'Exporting...' : 'Export CSV'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.print()}
                  aria-label="Print report"
                >
                  Print
                </Button>
              </div>
            </div>

            {exportError && (
              <div
                role="alert"
                className="p-3 mb-4 bg-destructive/5 border border-destructive/20 rounded-md text-destructive text-sm"
              >
                {exportError}
              </div>
            )}

            {/* Summary stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5 p-4 bg-accent/50 rounded-md">
              <div>
                <p className="m-0 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Total Expenses
                </p>
                <p className="mt-1 m-0 text-3xl font-bold text-foreground">
                  {report.expenseCount}
                </p>
              </div>
              <div>
                <p className="m-0 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Total Amount
                </p>
                <p
                  className="mt-1 m-0 text-3xl font-bold text-foreground"
                  data-testid="expense-report-total"
                  aria-label={`Total amount: ${formatAmount(report.totalAmount)}`}
                >
                  {formatAmount(report.totalAmount)}
                </p>
              </div>
              <div>
                <p className="m-0 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Categories
                </p>
                <p className="mt-1 m-0 text-3xl font-bold text-foreground">
                  {report.categories.length}
                </p>
              </div>
            </div>

            {report.categories.length === 0 ? (
              <div className="py-8 px-4 text-center text-muted-foreground text-sm">
                No expenses found for {report.year}.
              </div>
            ) : (
              <ReportTable report={report} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ExpenseReportPage;
